import type { AppState } from '../state/store';
import { versionedAssetUrl } from '../assets/version';
import { btn, esc } from './components';

/**
 * Public "How To Play" beginner guide (separate from the full Docs reference).
 * Short, visual, first-session focused: what to click, what the loop is, and
 * what the dungeon icons mean. Deep rules live in the Docs view.
 */

interface SpriteCard {
  icon: string;
  /** Monster art ships as a horizontal sprite sheet; render frame 1 only. */
  sheet?: boolean;
  title: string;
  note: string;
}

function sprite(icon: string, options: { sheet?: boolean; size?: 'lg' } = {}): string {
  const sizeClass = options.size === 'lg' ? ' htp-sprite--lg' : '';
  if (options.sheet) {
    return `<span class="htp-sprite htp-sprite--sheet${sizeClass}" style="background-image:url('${esc(versionedAssetUrl(icon))}')" aria-hidden="true"></span>`;
  }
  return `<img class="htp-sprite${sizeClass}" src="${esc(versionedAssetUrl(icon))}" alt="" loading="lazy" decoding="async" aria-hidden="true">`;
}

function spriteCards(cards: SpriteCard[], extraClass = ''): string {
  const items = cards
    .map(
      (card) => `
        <li class="htp-card">
          ${sprite(card.icon, { sheet: card.sheet })}
          <div>
            <strong>${esc(card.title)}</strong>
            <span>${esc(card.note)}</span>
          </div>
        </li>`,
    )
    .join('');
  return `<ul class="htp-cards ${esc(extraClass)}">${items}</ul>`;
}

const LOOP_STEPS: SpriteCard[] = [
  { icon: '/assets/sprites/hub/hub_station_dungeon_gate_01.png', title: 'Descend', note: 'Leave the Waystation through the Dungeon Gate.' },
  { icon: '/assets/sprites/items/pickups/pickup_gold_cache_01.png', title: 'Loot', note: 'Pick up Gold caches and item chests as you explore.' },
  { icon: '/assets/sprites/ui/ui_lantern_01.png', title: 'Survive', note: 'Watch HP and lantern fuel — burden makes the dark meaner.' },
  { icon: '/assets/sprites/environment/props/prop_ferry_step_ferryman_01.png', title: 'Extract', note: 'Reach a ferry step on floor 3 or deeper to bank your haul.' },
  { icon: '/assets/sprites/hub/hub_station_marketplace_02.png', title: 'Spend & trade', note: 'Gear up at the Merchant, Marketplace, and Inventory.' },
  { icon: '/assets/sprites/environment/props/prop_descent_stair_01.png', title: 'Go deeper', note: 'Better rarities seal only on deeper floors. Repeat.' },
];

const DUNGEON_LEGEND: SpriteCard[] = [
  { icon: '/assets/sprites/ui/ui_hp_01.png', title: 'HP', note: 'Run health. At zero you die and drop everything you carry.' },
  { icon: '/assets/sprites/ui/ui_lantern_01.png', title: 'Lantern', note: 'Fuel drains every turn. An empty lantern is how runs end.' },
  { icon: '/assets/sprites/items/consumables/supply_oil_flask_01.png', title: 'Oil Flask', note: 'Refuels the lantern by +5. Finite charges per run.' },
  { icon: '/assets/sprites/items/consumables/supply_torch_bundle_01.png', title: 'Torch Bundle', note: 'Smaller +3 fuel top-up. Cheap and cheerful.' },
  { icon: '/assets/sprites/items/consumables/supply_healing_draught_01.png', title: 'Healing Draught', note: 'Heals +4 HP. Using a supply still costs a turn.' },
  { icon: '/assets/sprites/ui/ui_burden_01.png', title: 'Burden', note: 'Carried Gold and items weigh you down and raise the danger.' },
  { icon: '/assets/sprites/monsters/monster_001/monster_001.png', sheet: true, title: 'Monsters', note: 'Fight or route around them. Damage adds up fast.' },
  { icon: '/assets/sprites/items/pickups/pickup_gold_cache_01.png', title: 'Gold cache', note: 'Floor Gold. Banks only if you extract with it.' },
  { icon: '/assets/sprites/items/pickups/pickup_item_chest_01.png', title: 'Item chest', note: 'Gear and relics. Deeper floors roll better rarities.' },
  { icon: '/assets/sprites/environment/props/prop_descent_stair_01.png', title: 'Descent stair', note: 'Takes you one floor deeper. There is no way back up.' },
  { icon: '/assets/sprites/environment/props/prop_ferry_step_01.png', title: 'Ferry step', note: 'The exit. Appears from floor 3 down — extract here.' },
];

const FIRST_SESSION_STEPS: Array<{ title: string; note: string }> = [
  { title: 'Connect your wallet', note: 'Sign one login message. Signing in is free — no gas, no transaction.' },
  { title: 'Hold $OBOL for eligibility', note: 'Reward runs are token-gated. Echo runs and Spectate work without it.' },
  { title: 'Re-check on the Dashboard', note: 'Just bought or moved tokens? Use Re-check to refresh your eligibility.' },
  { title: 'Enter the Waystation', note: 'The hub. Walk with WASD or arrows, press E at a station to use it.' },
  { title: 'Practice with an Echo run', note: 'Free, no Ferry Pass, no risk — and nothing you find is kept.' },
  { title: 'Spend a Ferry Pass on a Reward run', note: 'The real loop. Passes refill over time up to a cap of 6.' },
  { title: 'Extract at floor 3 or deeper', note: 'Reach a ferry step to bank Gold and seal items. Dying loses the haul.' },
  { title: 'Visit Inventory and the Merchant', note: 'Equip what you kept, restock supplies, repair your gear.' },
];

function loopStrip(): string {
  const steps = LOOP_STEPS.map(
    (step, index) => `
      <li class="htp-loop__step">
        ${sprite(step.icon, { size: 'lg' })}
        <strong>${esc(step.title)}</strong>
        <span>${esc(step.note)}</span>
        ${index < LOOP_STEPS.length - 1 ? '<span class="htp-loop__arrow" aria-hidden="true">→</span>' : '<span class="htp-loop__arrow htp-loop__arrow--repeat" aria-hidden="true">↺</span>'}
      </li>`,
  ).join('');
  return `<ol class="htp-loop">${steps}</ol>`;
}

function firstSessionSteps(): string {
  const steps = FIRST_SESSION_STEPS.map(
    (step) => `
      <li>
        <strong>${esc(step.title)}</strong>
        <span>${esc(step.note)}</span>
      </li>`,
  ).join('');
  return `<ol class="htp-steps">${steps}</ol>`;
}

function heroSection(state: AppState): string {
  const playCta = state.session
    ? btn('Enter the Waystation', 'goto-waystation', {}, { variant: 'primary' })
    : btn('Connect Wallet', 'goto-dashboard', {}, { variant: 'primary' });
  return `
    <header class="htp-hero">
      <div class="htp-hero__copy">
        <p class="docs-section__eyebrow">Player guide</p>
        <h1>How to Play Gravelight</h1>
        <p class="htp-hero__pitch">
          Enter the Waystation, descend into Echo or Reward runs, collect Gold and gear,
          then extract before your lantern runs out.
        </p>
        <div class="btn-row">
          ${playCta}
          ${btn('Full Docs', 'goto-docs', {}, { variant: 'ghost' })}
          ${btn('Spectate a Run', 'goto-spectate', {}, { variant: 'ghost' })}
        </div>
      </div>
      <div class="htp-hero__scene" aria-hidden="true">
        <img src="${esc(versionedAssetUrl('/assets/sprites/hub/hub_waystation_scene_01.png'))}" alt="" loading="eager" decoding="async">
      </div>
    </header>`;
}

function section(id: string, eyebrow: string, title: string, inner: string): string {
  return `
    <section class="htp-section" id="${esc(id)}">
      <p class="docs-section__eyebrow">${esc(eyebrow)}</p>
      <h2>${esc(title)}</h2>
      ${inner}
    </section>`;
}

function echoVsReward(): string {
  return `
    <div class="htp-compare">
      <article class="htp-compare__card">
        <h3>${sprite('/assets/sprites/hub/hub_station_dungeon_gate_01.png')} Echo run — practice</h3>
        <ul>
          <li>Free: no Ferry Pass and no token gate.</li>
          <li>Practice supplies, real dungeon rules.</li>
          <li>Nothing is kept — loot and Gold vanish when the Echo ends.</li>
          <li>The safe way to learn movement, combat, and the lantern.</li>
        </ul>
      </article>
      <article class="htp-compare__card htp-compare__card--reward">
        <h3>${sprite('/assets/sprites/ui/ui_ferry_pass_01.png')} Reward run — the real loop</h3>
        <ul>
          <li>Needs an eligible account and spends 1 Ferry Pass.</li>
          <li>Extract at floor 3+ to bank Gold and seal items.</li>
          <li>Die or abandon and the carried haul is gone.</li>
          <li>Passes refill +1 every 4 hours, up to 6.</li>
        </ul>
      </article>
    </div>`;
}

function pickupChoices(): string {
  return spriteCards(
    [
      { icon: '/assets/sprites/ui/ui_gold_01.png', title: 'Gold pays for everything', note: 'Supplies, repairs, cosmetics, and player trades — but carrying it raises burden.' },
      { icon: '/assets/sprites/items/pickups/pickup_common_weapon_01.png', title: 'Items are gear or value', note: 'Equip upgrades, or salvage and sell what you do not need.' },
      { icon: '/assets/sprites/environment/props/prop_descent_hole_stair_01.png', title: 'Depth is the gamble', note: 'Rare+ items only seal on deeper floors. Farm shallow or push deep — your call.' },
    ],
    'htp-cards--trio',
  );
}

function gearAndInventory(): string {
  return `
    <div class="htp-split">
      <div>
        <p>
          You have five gear slots: weapon, armor, lantern, and two trinkets. Gear only carries
          survival stats — attack, defense, HP, lantern efficiency, burden mitigation — never
          Gold or reward bonuses.
        </p>
        <ul>
          <li><strong>If you own it, you can equip it.</strong> Rarity never locks a slot — gear bought from other players works immediately.</li>
          <li>Seal Depth only decides whether <em>found</em> loot banks when you extract. It never blocks equipping owned gear.</li>
          <li>Equipping unbound dungeon gear binds it to your account (you confirm first). Bound gear cannot be traded, but can be salvaged.</li>
          <li>Your cosmetic collection also lives on the Inventory page.</li>
        </ul>
      </div>
      ${spriteCards([
        { icon: '/assets/sprites/items/pickups/pickup_common_weapon_01.png', title: 'Weapon', note: 'Attack' },
        { icon: '/assets/sprites/items/icons/item_common_armor_01.png', title: 'Armor', note: 'Defense and HP' },
        { icon: '/assets/sprites/items/icons/item_common_lantern_01.png', title: 'Lantern', note: 'Fuel and efficiency' },
        { icon: '/assets/sprites/items/icons/item_common_trinket_01.png', title: 'Trinkets ×2', note: 'Utility bonuses' },
      ], 'htp-cards--slots')}
    </div>`;
}

function suppliesAndMerchant(): string {
  return `
    <p>
      Every Reward run starts with a free base kit: 2 Oil, 2 Torches, 2 Healing Draughts.
      Supplies bought from the Merchant load as extras on top, up to a small per-run cap.
    </p>
    ${spriteCards([
      { icon: '/assets/sprites/merchant/merchant_station_01.png', title: 'Buy supplies', note: 'Oil, torches, and draughts for Gold. Stock is finite and restocks over time.' },
      { icon: '/assets/sprites/ui/ui_repair_hammer_01.png', title: 'Repair gear', note: 'Damaged gear is restored for a rarity-scaled Gold cost.' },
      { icon: '/assets/sprites/ui/ui_gold_01.png', title: 'Salvage junk', note: 'Turn unwanted gear (even bound gear) into a small fixed Gold return.' },
    ], 'htp-cards--trio')}
    <p class="htp-footnote">The Merchant never sells weapons, armor, or other gear — players trade those on the Marketplace.</p>`;
}

function markets(): string {
  return `
    <div class="htp-compare">
      <article class="htp-compare__card">
        <h3>${sprite('/assets/sprites/hub/hub_station_marketplace_02.png')} Marketplace</h3>
        <ul>
          <li>Buy and sell gear and cosmetics with other players.</li>
          <li>List in Gold (0% fee) or in $OBOL — the seller picks.</li>
          <li>$OBOL sales pay 95% to the seller and 5% to the treasury, verified on-chain before the item moves.</li>
          <li>Buying an $OBOL listing reserves it for 10 minutes while you pay from your wallet.</li>
        </ul>
      </article>
      <article class="htp-compare__card">
        <h3>${sprite('/assets/sprites/hub/hub_station_gold_exchange_01.png')} Gold Exchange</h3>
        <ul>
          <li>Sell banked Gold to other players for $OBOL, or buy theirs.</li>
          <li>Same 95% / 5% seller–treasury split.</li>
          <li>The buyer pays on-chain from their own wallet; the server verifies the payment before any Gold moves.</li>
        </ul>
      </article>
    </div>`;
}

function spinnerAndCosmetics(): string {
  return `
    <div class="htp-compare">
      <article class="htp-compare__card">
        <h3>${sprite('/assets/sprites/hub/hub_station_spinner_03.png')} Ferryman's Wager</h3>
        <ul>
          <li>Free spin (eligible accounts, on a cooldown): run supplies only.</li>
          <li>Paid spin: $5 of $OBOL at a live quote that stays payable for 2 minutes.</li>
          <li>Paid prizes: bigger supply bundles plus a weekly spin-exclusive cosmetic.</li>
          <li>The Spinner never pays out Gold, gear, or items.</li>
        </ul>
      </article>
      <article class="htp-compare__card">
        <h3>${sprite('/assets/sprites/hub/hub_station_cosmetics_01.png')} Cosmetics</h3>
        <ul>
          <li>Visual-only skins — zero stats, zero gameplay advantage.</li>
          <li>Claim campaigns, Gold shop skins, and spin-exclusive weeklies.</li>
          <li>Your collection lives in Inventory; tradeable skins sell on the Marketplace.</li>
        </ul>
      </article>
    </div>
    <div class="htp-skin-row" aria-hidden="true">
      ${sprite('/assets/sprites/cosmetics/cosmetic_skin_waystation_pilgrim_01.png', { size: 'lg' })}
      ${sprite('/assets/sprites/cosmetics/cosmetic_skin_blackhorn_warden_01.png', { size: 'lg' })}
      ${sprite('/assets/sprites/cosmetics/cosmetic_skin_wagerbound_ferryman_01.png', { size: 'lg' })}
      ${sprite('/assets/sprites/cosmetics/cosmetic_skin_graveway_scout_01.png', { size: 'lg' })}
      ${sprite('/assets/sprites/cosmetics/cosmetic_skin_ashwing_herald_01.png', { size: 'lg' })}
    </div>`;
}

function safety(): string {
  return `
    <ul class="htp-safety">
      <li><strong>Never enter your seed phrase.</strong> Gravelight only ever asks your wallet to sign — it never asks for keys.</li>
      <li><strong>Read every wallet popup.</strong> Signatures and payments should match an action you just started.</li>
      <li><strong>No promises.</strong> Random rewards are not guaranteed value, token prices move, and rules can be tuned during staging.</li>
    </ul>`;
}

export function renderHowToPlay(state: AppState): string {
  return `
    <div class="htp-shell">
      ${heroSection(state)}
      ${section('first-session', 'Step by step', 'Your first ten minutes', firstSessionSteps())}
      ${section('core-loop', 'The heartbeat', 'The core loop', loopStrip())}
      ${section('echo-vs-reward', 'Two ways down', 'Echo vs Reward', echoVsReward())}
      ${section('dungeon-basics', 'Field guide', 'Reading the dungeon', spriteCards(DUNGEON_LEGEND, 'htp-cards--legend'))}
      ${section('what-to-grab', 'Greed management', 'What to pick up', pickupChoices())}
      ${section('gear', 'Loadout', 'Gear and Inventory', gearAndInventory())}
      ${section('supplies', 'Restock', 'Supplies and the Merchant', suppliesAndMerchant())}
      ${section('markets', 'Player economy', 'Marketplace and Gold Exchange', markets())}
      ${section('spinner-cosmetics', 'Extras', 'Spinner and Cosmetics', spinnerAndCosmetics())}
      ${section('safety', 'Stay safe', 'Before you sign anything', safety())}
      <footer class="htp-more">
        <p>Want exact rules, seal depths, fees, and FAQ?</p>
        ${btn('Read the full Docs', 'goto-docs', {}, { variant: 'primary' })}
      </footer>
    </div>`;
}
